import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SUPPRESS_HTTP_ERROR_TOAST } from 'app/core/interceptors';
import { ReleaseNoteModel } from 'app/core/models';
import { environment } from 'src/environments';
import { BaseLoadTableResult } from './baseLoadTableResult';

/** The release-notes API route prefix shared by client operations. */
export const RELEASE_NOTES_API_ROUTE_PREFIX = 'release-notes';

const basePath = `${environment.apiBaseUrl}${RELEASE_NOTES_API_ROUTE_PREFIX}`;

/** The only file that talks to the release-notes API. */
@Injectable({ providedIn: 'root' })
export class ReleaseNotesApiClient {
  private readonly _http = inject(HttpClient);

  /**
   * Atomically claims the right to announce the latest release to this user.
   *
   * Emits the release when this call won the claim, or `null` when nothing is published or the
   * latest release was already claimed — on any device, in any tab. The server decides with one
   * conditional upsert, so two concurrent calls cannot both win.
   *
   * Errors are not toasted: this fires on page load without the user having asked for anything, so a
   * failure should stay silent and be retried on a later navigation rather than interrupt them.
   */
  claimLatest(): Observable<ReleaseNoteModel | null> {
    return this._http.post<ReleaseNoteModel | null>(`${basePath}/latest/claim`, null, {
      context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true),
    });
  }

  /**
   * One page of releases, newest first, bodies included. `startingAfter` is the `releasedAt` of the
   * last row already loaded — a cursor rather than an offset, so a release published mid-scroll
   * cannot duplicate or hide a row.
   */
  list(startingAfter: string | null, limit: number): Observable<BaseLoadTableResult<ReleaseNoteModel>> {
    let params: HttpParams = new HttpParams().set('limit', limit);
    if (startingAfter !== null) {
      params = params.set('startingAfter', startingAfter);
    }

    return this._http.get<BaseLoadTableResult<ReleaseNoteModel>>(basePath, { params });
  }
}
